import { useAppDispatch, useAppSelector } from "hooks";
import { createContext, ReactNode, useContext, useEffect } from "react";
import { browserName, isMobile } from "react-device-detect";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { generateSessionId, selectWebStoreState } from "redux/slices";
import {
	AbstractHttpService,
	createHttpService
} from "../infrastructure/HttpService";
import { GSNHeader } from "../infrastructure/HttpService/headerConstants";
import { getDebugLogger } from "../utils/debugLogger";
import { useEnvContext } from "./EnvProvider";
import { useGoogleAnalyticsContext } from "./GoogleAnalyticsProvider";
import sequenceInstance from "./SessionSequenceProvider";

const debugColor = "Cyan";
const debug = getDebugLogger({
	isEnabled: !false,
	color: debugColor
});

export interface IWebstoreHttpServiceContext {
	webstoreHttpService: AbstractHttpService;
}

export const WebstoreHttpServiceContext =
	createContext<IWebstoreHttpServiceContext>({
		webstoreHttpService: createHttpService(debug)
	});

const WebstoreHttpServiceProvider = ({ children }: { children: ReactNode }) => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { backendUri } = useEnvContext();
	const deviceType = isMobile ? "mobile" : "desktop";
	const browserType = browserName;
	const [searchParams] = useSearchParams();
	const { syntheticId } = useGoogleAnalyticsContext();
	const { sessionId } = useAppSelector(selectWebStoreState);

	const httpService = createHttpService(debug);
	httpService.setLogContext("Webstore Http Service");
	httpService
		.setDefaultHeader(GSNHeader.DeviceType, deviceType)
		.setDefaultHeader(GSNHeader.BrowserType, browserType)
		.setDefaultHeader(GSNHeader.SyntheticId, syntheticId)
		.setDefaultHeader(GSNHeader.SessionId, sessionId)
		.setDefaultHeader(
			GSNHeader.sessionSequence,
			sequenceInstance.incrementAndGetSequence().toString()
		)
		.setDefaultBackendUrl(backendUri);

	useEffectOnce(() => {
		const sessionIdFromUrl = searchParams.get("sessionId") || undefined;
		if (sessionIdFromUrl) {
			searchParams.delete("sessionId");
			navigate(`${location.pathname}?${searchParams.toString()}`);
		}
		dispatch(generateSessionId(sessionIdFromUrl));
	});

	// If any of these values change, update httpService
	useEffect(() => {
		httpService
			.setDefaultHeader(GSNHeader.DeviceType, deviceType)
			.setDefaultHeader(GSNHeader.BrowserType, browserType)
			.setDefaultHeader(GSNHeader.SyntheticId, syntheticId)
			.setDefaultHeader(GSNHeader.SessionId, sessionId)
			.setDefaultHeader(
				GSNHeader.sessionSequence,
				sequenceInstance.incrementAndGetSequence().toString()
			)
			.setDefaultBackendUrl(backendUri);
	}, [backendUri, deviceType, syntheticId, browserType, sessionId]);

	const value: IWebstoreHttpServiceContext = {
		webstoreHttpService: httpService
	};

	return (
		<>
			{sessionId !== "" && (
				<WebstoreHttpServiceContext.Provider value={value}>
					{children}
				</WebstoreHttpServiceContext.Provider>
			)}
		</>
	);
};

export const useWebstoreHttpService = () => {
	const context = useContext(WebstoreHttpServiceContext);
	if (context === undefined) {
		throw new Error(
			"WebstoreHttpServiceContext was used outside of its Provider"
		);
	}
	return context;
};

export default WebstoreHttpServiceProvider;
